import { memo } from 'react';
import { useIntl } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useCopyToClipboard } from '@rossum/components/copy-to-clipboard/useCopyToClipboard';
import { IconCheck } from '@rossum/ui/icons/tabler';
import {
  Button,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { NonAdminRestrictor } from '../../../components/Restrictors';
import { ROSSUM_EMAIL_INBOX } from '../../../constants/values';
import { link } from '../../../lib/formaterValues';

type SuggestInboxProps = {
  inboxEmail?: string;
  queueId?: number;
  isLoading: boolean;
  showHelpArticle?: boolean;
};

export const SuggestInbox = memo(
  ({
    inboxEmail,
    queueId,
    isLoading,
    showHelpArticle = true,
  }: SuggestInboxProps) => {
    const intl = useIntl();
    const { copied, copyToClipboard } = useCopyToClipboard(inboxEmail || '');

    if (!isLoading && !inboxEmail) {
      return (
        <Stack spacing={2} alignItems="center">
          <Typography variant="subtitle2" color="text.secondary">
            {intl.formatMessage({
              id: 'containers.annotationList.empty.suggestInbox.noInbox.hint',
            })}
          </Typography>
          {queueId && (
            <NonAdminRestrictor
              restrictComponent={
                <Typography variant="subtitle2" color="text.secondary">
                  {intl.formatMessage({
                    id: 'containers.annotationList.empty.suggestInbox.noInbox.restricted',
                  })}
                </Typography>
              }
            >
              <Button
                variant="contained"
                component={RouterLink}
                to={`/queues/${queueId}/settings/emails`}
                sx={{
                  '&:hover': {
                    color: theme => theme.palette.primary.contrastText,
                  },
                }}
              >
                {intl.formatMessage({
                  id: 'containers.annotationList.empty.suggestInbox.noInbox.button',
                })}
              </Button>
            </NonAdminRestrictor>
          )}
        </Stack>
      );
    }

    return (
      <Stack alignItems="center" spacing={3}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={copyToClipboard}
          sx={{ cursor: 'pointer' }}
          data-cy="copy-email-address"
        >
          <Button variant="outlined" disabled>
            <Stack direction="row" sx={{ minWidth: 0 }}>
              <Typography
                variant="button"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  flexShrink: 1,
                }}
              >
                {isLoading ? <Skeleton width={250} /> : inboxEmail}
              </Typography>
            </Stack>
          </Button>
          <Button
            color={copied ? 'success' : 'secondary'}
            variant="outlined"
            size="medium"
            disabled={isLoading}
            sx={{ minWidth: '134px' }}
            startIcon={
              copied ? (
                <SvgIcon fontSize="medium">
                  <IconCheck />
                </SvgIcon>
              ) : null
            }
          >
            {intl.formatMessage({
              id: copied
                ? 'containers.annotationList.empty.suggestInbox.copyInfo'
                : 'containers.annotationList.empty.suggestInbox.copyEmail',
            })}
          </Button>
        </Stack>
        {showHelpArticle ? (
          <Typography variant="caption" color="text.secondary">
            {intl.formatMessage(
              {
                id: 'containers.annotationList.empty.suggestInbox.sendEmail',
              },
              {
                link: link(ROSSUM_EMAIL_INBOX),
              }
            )}
          </Typography>
        ) : null}
      </Stack>
    );
  }
);
